import api from './api'
import type { AccessContext, Organization, OrganizationType } from '../types/access'
import type { IndustryType } from '../domain/industryPresets'

export interface AdminOnboardingPayload {
  name: string
  email: string
  password: string
  phone?: string
  industryType?: IndustryType | null
  organization: {
    type: Extract<OrganizationType, 'agency' | 'company'>
    name: string
    slug: string
    businessEmail?: string
    businessPhone?: string
    timezone?: string
    address?: string
  }
  referralAttribution?: {
    referralCode?: string | null
    referralClickId?: string | null
    inviteCode?: string | null
    utmSource?: string | null
    utmMedium?: string | null
    utmCampaign?: string | null
  } | null
}

export interface AdminOnboardingResponse {
  authenticationState?: AuthenticationState
  restricted?: boolean
  success: boolean
  message?: string
  userId: string
  tenantId?: string
  token: string
  profile?: {
    email?: string
    firstName?: string | null
    lastName?: string | null
    phone?: string | null
    role?: string
  } | null
  organization?: Organization
  onboarding?: {
    accountType?: string
    organizationId?: string
    organizationType?: string
    organizationSlug?: string | null
    scopedRoleStatus?: string
    signupAttribution?: Record<string, any> | null
  }
  industryType?: string | null
}

export interface AdminOnboardingResult extends AdminOnboardingResponse {
  accessContext?: AccessContext | null
  accessContextError?: string | null
}

export type AuthenticationState = 'authenticated' | 'mfa_required' | 'mfa_enrollment_required'

/**
 * Pending privileged MFA enrollment material returned by
 * `POST /api/auth/mfa/enrollment/start`. This lives in component memory only —
 * it must never be persisted to browser storage, logged, or sent to a
 * third-party QR service. See MfaEnrollment.tsx.
 */
export interface MfaEnrollmentDetails {
  /** The base32 TOTP setup key. Shown, masked, only during pending enrollment. */
  secret: string
  /** The `otpauth://totp/...` URI. Used solely as the local QR code value. */
  otpauthUri: string
  issuer: string
  accountName: string
  expiresInSeconds: number
}

export interface MfaEnrollmentStartResponse {
  success: boolean
  enrollment: MfaEnrollmentDetails
}

export interface AuthenticationResponse {
  success: boolean
  authenticationState?: AuthenticationState
  token?: string
  challengeToken?: string
  expiresInSeconds?: number
  restricted?: boolean
  profile?: any
  user?: any
  message?: string
  recoveryCodes?: string[]
  recoveryCodeCount?: number
}

export interface OrganizationInvitePreview {
  acceptanceMode?: 'new_account' | 'existing_account' | 'unavailable'
  id: string
  tenantId: string
  tenantName?: string
  tenantSlug?: string
  orgId: string
  organizationName?: string
  organizationSlug?: string
  organizationType?: string
  email: string
  roleLabel?: string
  roleKey?: string
  scopeType?: string
  scopeId?: string
  expiresAt?: string
}

export const authService = {
  login: async (email: string, password: string) => {
    const selected = new URLSearchParams(window.location.search).get('tenantId')
    const tenantId = selected && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(selected) ? selected : undefined
    const payload = { email, password, ...(tenantId ? { tenantId } : {}) }
    try {
      return await api.post('/api/auth/login', payload)
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status
      if (status !== 404 && status !== 405) throw error
      return api.post('/auth/login', payload)
    }
  },

  signup: (email: string, password: string) =>
    api.post('/auth/signup', { email, password }),

  registerAdminOnboarding: (payload: AdminOnboardingPayload) =>
    api.post<AdminOnboardingResponse>('/api/auth/register', { ...payload, accountType: 'admin' }),

  resolveReferralCode: (code: string) =>
    api.get('/api/referrals/resolve', { params: { code } }),

  captureReferralClick: (payload: any) =>
    api.post('/api/referrals/click', payload),

  acceptSalesRepInvite: (payload: { inviteCode: string; email?: string | null }) =>
    api.post('/api/sales-rep-invites/accept', payload),

  getOrganizationInvite: async (token: string): Promise<OrganizationInvitePreview> => {
    const response = await api.get(`/api/org-invites/${token}`)
    return response.data?.invite as OrganizationInvitePreview
  },

  acceptOrganizationInvite: (token: string, payload: { firstName?: string; lastName?: string; phone?: string; password?: string }) =>
    api.post(`/api/org-invites/${token}/accept`, payload),

  startPasswordRecovery: (email: string) => api.post('/api/auth/password-reset/start', { email }),

  completePasswordRecovery: (token: string, newPassword: string) => api.post('/api/auth/password-reset/complete', { token, newPassword }),

  logout: () => api.post('/auth/logout'),

  getSession: () => api.get('/auth/session'),

  verifyMfaChallenge: (challengeToken: string, code: string) =>
    api.post<AuthenticationResponse>('/api/auth/mfa/challenge/verify', { challengeToken, code }),

  getMfaStatus: () => api.get('/api/auth/mfa/status'),
  getTenantMfaPolicy: () => api.get('/api/auth/tenant-policy'),
  updateTenantMfaPolicy: (policy: string) => api.patch('/api/auth/tenant-policy', { policy }),

  startMfaEnrollment: () =>
    api.post('/api/auth/mfa/enrollment/start'),

  confirmMfaEnrollment: (code: string) =>
    api.post<AuthenticationResponse>('/api/auth/mfa/enrollment/confirm', { code }),

  listSessions: () => api.get('/api/auth/sessions'),

  revokeSession: (sessionId: string) => api.delete(`/api/auth/sessions/${sessionId}`),

  revokeOtherSessions: () => api.post('/api/auth/sessions/revoke-others'),

  revokeAllSessions: () => api.post('/api/auth/sessions/revoke-all'),
}
